import React, { useEffect, useState, useRef } from 'react';
import { useAccount } from '../context/AccountContext';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faCheck, faSpinner } from '@fortawesome/free-solid-svg-icons';

export const AccountSwitcher: React.FC = () => {
    const { activeAccount, setActiveAccount, availableAccounts, setAvailableAccounts } = useAccount();
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [switching, setSwitching] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        loadAccounts();
    }, [activeAccount]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadAccounts = async () => {
        try {
            const response = await axios.get('/pool/api/user/aliases');
            setAvailableAccounts(response.data);

            const savedAccount = localStorage.getItem('activeAccount');
            const accountExists = response.data.some((acc: any) => acc.userName === savedAccount);

            if (!savedAccount || !accountExists) {
                const primary = response.data.find((acc: any) => acc.isPrimary);
                if (primary) {
                    setActiveAccount(primary.userName);
                }
            }
        } catch (error) {
            console.error('Failed to load accounts:', error);
        }
    };

    const handleAccountChange = async (userName: string) => {
        if (userName === activeAccount) {
            setIsOpen(false);
            return;
        }

        setSwitching(true);
        setActiveAccount(userName);
        axios.defaults.headers.common["X-Active-Account"] = userName;

        // Small delay for visual feedback, then close dropdown
        setTimeout(() => {
            setSwitching(false);
            setIsOpen(false);
            setSearchTerm('');
        }, 400);
    };

    const filteredAccounts = availableAccounts?.filter(account =>
        account.userName.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (availableAccounts.length <= 1) {
        return null;
    }

    return (
        <div className="account-switcher relative" style={{ marginRight: '1rem' }} ref={dropdownRef}>
            <button
                onClick={() => !switching && setIsOpen(!isOpen)}
                disabled={switching}
                className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
                {switching ? (
                    <>
                        <FontAwesomeIcon icon={faSpinner} className="text-sm text-blue-500 dark:text-blue-400 animate-spin" />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Switching...</span>
                    </>
                ) : (
                    <>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                            {activeAccount || 'Select Account'}
                        </span>
                        <svg
                            className={`w-4 h-4 text-gray-500 dark:text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </>
                )}
            </button>

            {isOpen && !switching && (
                <div className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-2xl z-50 animate-in fade-in slide-in-from-top-2 duration-200">
                    {/* Search Input */}
                    <div className="p-3 border-b border-gray-200 dark:border-slate-700">
                        <div className="relative">
                            <FontAwesomeIcon
                                icon={faSearch}
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 text-sm"
                            />
                            <input
                                type="text"
                                placeholder="Please input account or Desc."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                                autoFocus
                            />
                        </div>
                    </div>

                    {/* Account List */}
                    <div className="max-h-64 overflow-y-auto">
                        {filteredAccounts.length > 0 ? (
                            filteredAccounts.map(account => (
                                <button
                                    key={account.userName}
                                    onClick={() => handleAccountChange(account.userName)}
                                    className={`w-full flex items-center justify-between px-4 py-3 transition-all text-left border-b border-gray-100 dark:border-slate-700 last:border-b-0 ${activeAccount === account.userName
                                        ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                                        : 'hover:bg-gray-50 dark:hover:bg-slate-700'
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className={`text-sm font-medium ${activeAccount === account.userName
                                                    ? 'text-blue-700 dark:text-blue-300'
                                                    : 'text-gray-700 dark:text-gray-200'
                                                    }`}>
                                                    {account.userName}
                                                </span>
                                                {account.isPrimary && (
                                                    <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 rounded">
                                                        Primary
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {activeAccount === account.userName && (
                                        <FontAwesomeIcon icon={faCheck} className="text-blue-600 dark:text-blue-400 text-sm" />
                                    )}
                                </button>
                            ))
                        ) : (
                            <div className="px-4 py-8 text-center text-gray-500 dark:text-gray-400 text-sm">
                                No accounts found
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
